import {
	Button,
	Checkbox,
	FormControlLabel,
	FormGroup,
	InputLabel,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
} from "@mui/material";
import { FontControlGroup } from "./FontControlGroup.component";
import { usePdfTemplateConfigurationContext } from "../../context/PdfTemplateConfigurationContext";
import { Alignment } from "../../pdf-templates/pdf-templates/types";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import RichTextEditor from "@mantine/rte";
import { compressImage } from "@/lib/utils/compress-image";
import { v4 as uuidv4 } from "uuid";
import { useCentralStore } from "../../store/Central";
import {
	AccordionItem,
	AccordionTrigger,
	AccordionContent,
} from "../../components/ui/accordion";
import { CustomTextFieldWithSaveButton } from "../../forms/form-input-components/custom-input-components/custom-text-field-with-save-button.component";
import { PDFRtf } from "./pdf-rtf.component";
import { useState } from "react";
import { showNotification } from "../../store/Central/selectors";
import emptyImage from "../../assets/empty.jpg";
import { Info } from "lucide-react";
import { uploadFileWithPublicUrl } from "../../hooks/useStorage";

export const PDFTemplateConfigurationSettings = ({
	template,
}: {
	template: PdfTemplateType | "general";
}) => {
	const { updatePdfTemplateConfiguration, pdfTemplateConfiguration } =
		usePdfTemplateConfigurationContext();

	const config = pdfTemplateConfiguration?.[template];
	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));
	const [customImg, setCustomImg] = useState<string | null>(null);

	if (!config) return;

	const upload = async (file: File) => {
		const compressedFile = await compressImage(file, 35);
		const path = `${organization?.id}/${uuidv4()}/${file.name}`;
		const { success, pathName, url } = await uploadFileWithPublicUrl(
			path,
			compressedFile
		);

		if (success && pathName && url) {
			updatePdfTemplateConfiguration("general", {
				customIconPath: pathName,
				customImgUrl: url,
			});
		}
	};

	return (
		<>
			{" "}
			<AccordionItem value="item-1">
				<AccordionTrigger>Kopfzeile und Laboradresse</AccordionTrigger>
				<AccordionContent>
					<div className="flex mt-4  flex-col gap-4">
						<div className="flex flex-col gap-2">
							<CustomTextFieldWithSaveButton
								label="E-Mail Adresse"
								value={pdfTemplateConfiguration?.general?.email}
								onSave={(value) => {
									updatePdfTemplateConfiguration("general", {
										email: value,
									});
								}}
								validationError={null}
								fullWidth
							/>
							<CustomTextFieldWithSaveButton
								label="Website"
								value={
									pdfTemplateConfiguration?.general?.website
								}
								onSave={(value) => {
									updatePdfTemplateConfiguration("general", {
										website: value,
									});
								}}
								validationError={null}
								fullWidth
							/>
						</div>
						<div className="flex flex-col gap-2">
							<InputLabel id="Address alignment">
								Adresse Text
							</InputLabel>

							<ToggleButtonGroup
								color="primary"
								value={config.addressRtfEnabled}
								exclusive
								onChange={(e, v) => {
									updatePdfTemplateConfiguration(template, {
										addressRtfEnabled: v as boolean,
									});
								}}
								aria-label="Platform"
							>
								<ToggleButton value={false}>
									Standard
								</ToggleButton>
								<ToggleButton value={true}>
									Individuell
								</ToggleButton>
							</ToggleButtonGroup>
							{config.addressRtfEnabled ? (
								<PDFRtf
									value={config.addressRtfText}
									controls={[
										[
											"italic",
											"underline",
											"bold",
											"alignLeft",
											"alignCenter",
											"alignRight",
										],
										["h1", "h2", "h3", "h4", "h5", "h6"],
										["image"],
									]}
									onSave={(val: string, width: number) => {
										if (!config.addressRtfEnabled) return;
										updatePdfTemplateConfiguration(
											template,
											{
												addressRtfText: {
													text: val,
													width: width,
												},
											}
										);
									}}
								/>
							) : (
								<>
									<InputLabel id="Address alignment">
										Adressausrichtung
									</InputLabel>
									<ToggleButtonGroup
										color="primary"
										value={
											config.oneLiner
												? "one-liner"
												: config.addressAlignment
										}
										exclusive
										onChange={(e, v) => {
											if (v == "one-liner") {
												updatePdfTemplateConfiguration(
													template,
													{
														oneLiner: true,
													}
												);
												return;
											}
											updatePdfTemplateConfiguration(
												template,
												{
													addressAlignment:
														v as Alignment,
													oneLiner: false,
												}
											);
										}}
										aria-label="Platform"
									>
										<ToggleButton value="left">
											Links
										</ToggleButton>
										<ToggleButton value="right">
											Rechts
										</ToggleButton>
										<ToggleButton value="one-liner">
											Einzeiler
										</ToggleButton>
									</ToggleButtonGroup>
								</>
							)}
							<FormControlLabel
								className="mb-1"
								control={
									<Checkbox
										checked={config.hideLabAddress}
										onChange={(e) => {
											updatePdfTemplateConfiguration(
												template,
												{
													hideLabAddress:
														e.target.checked,
												}
											);
										}}
									/>
								}
								label="Ausblenden"
							/>
							{template === "general" && (
								<FormControlLabel
									className="mb-1"
									control={
										<Checkbox
											checked={
												pdfTemplateConfiguration
													?.general
													?.senderAddressAboveRecipient ??
												false
											}
											onChange={(e) => {
												updatePdfTemplateConfiguration(
													"general",
													{
														senderAddressAboveRecipient:
															e.target.checked,
													}
												);
											}}
										/>
									}
									label="Absenderadresse einzeilig über Empfängeradresse"
								/>
							)}
							<FormControlLabel
								className="mb-1"
								control={
									<Checkbox
										checked={config.showBorder}
										onChange={(e) => {
											updatePdfTemplateConfiguration(
												template,
												{
													showBorder:
														e.target.checked,
												}
											);
										}}
									/>
								}
								label="Trennstrich"
							/>
							<div>
								<CustomTextFieldWithSaveButton
									label="Abstand nach unten in mm"
									value={config.headerBodyGap}
									onSave={(value) => {
										updatePdfTemplateConfiguration(
											template,
											{
												headerBodyGap: parseInt(value),
											}
										);
									}}
									validationError={null}
									fullWidth
								/>
							</div>
							<div>
								<CustomTextFieldWithSaveButton
									label="Adresse nach oben in mm"
									value={config.senderAddressUpShift}
									onSave={(value) => {
										updatePdfTemplateConfiguration(
											template,
											{
												senderAddressUpShift:
													parseInt(value),
											}
										);
									}}
									validationError={null}
									fullWidth
								/>
							</div>
							<FontControlGroup
								fontSizeValue={config.senderAddressFontSize}
								fontSizeProperty="senderAddressFontSize"
								template={template}
								updatePdfTemplateConfiguration={
									updatePdfTemplateConfiguration
								}
							/>
						</div>
					</div>
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="item-2">
				<AccordionTrigger>Empfängeradresse </AccordionTrigger>
				<AccordionContent>
					<div className="flex flex-col gap-4 mt-4 pb-4">
						<InputLabel id="Address alignment">
							Ausrichtung
						</InputLabel>
						<ToggleButtonGroup
							color="primary"
							value={config.recipientAddressAlignment}
							exclusive
							onChange={(e, v) => {
								updatePdfTemplateConfiguration(template, {
									recipientAddressAlignment: v as Alignment,
								});
								if (!config.idOneLiner) {
									const opposite = {
										left: "right",
										right: "left",
									};

									const newAlignment =
										opposite[v as "left" | "right"];

									updatePdfTemplateConfiguration(template, {
										idAlignment: newAlignment as
											| "left"
											| "right",
									});
								}
							}}
							aria-label="Platform"
						>
							<ToggleButton value="left">Links</ToggleButton>
							<ToggleButton value="right">Rechts</ToggleButton>
						</ToggleButtonGroup>
						<CustomTextFieldWithSaveButton
							label="Abstand nach oben in mm"
							value={config.recipientAddressTopGap}
							onSave={(value) => {
								updatePdfTemplateConfiguration(template, {
									recipientAddressTopGap: parseInt(value),
								});
							}}
							validationError={null}
							fullWidth
						/>
						<CustomTextFieldWithSaveButton
							label="Abstand nach rechts in mm"
							value={config.recipientAddressRightGap}
							onSave={(value) => {
								updatePdfTemplateConfiguration(template, {
									recipientAddressRightGap: parseInt(value),
								});
							}}
							validationError={null}
							fullWidth
						/>
						<FontControlGroup
							fontSizeValue={config.recipientAddressFontSize}
							fontSizeProperty="recipientAddressFontSize"
							template={template}
							updatePdfTemplateConfiguration={
								updatePdfTemplateConfiguration
							}
						/>
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										checked={
											config.absoluteRecipientAddress
										}
										onChange={(e) => {
											updatePdfTemplateConfiguration(
												template,
												{
													absoluteRecipientAddress:
														e.target.checked,
												}
											);
										}}
									/>
								}
								label="Absolute Position für Fensterkuvert"
							/>
						</FormGroup>
					</div>
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="item-3">
				<AccordionTrigger>Identifikationsnummern</AccordionTrigger>
				<AccordionContent className="flex flex-col gap-4 mt-4 pb-4">
					<InputLabel id="Address alignment">Platzierung</InputLabel>
					<ToggleButtonGroup
						color="primary"
						value={config.idOneLiner}
						exclusive
						onChange={(e, v) => {
							let newAlignment;
							if (!v) {
								if (
									config.idAlignment ==
									config.recipientAddressAlignment
								) {
									const opposite = {
										left: "right",
										right: "left",
									};

									newAlignment =
										opposite[
											config.recipientAddressAlignment as
												| "left"
												| "right"
										];
								}
							}
							updatePdfTemplateConfiguration(template, {
								idOneLiner: v as boolean,
								...(newAlignment
									? {
											idAlignment: newAlignment,
										}
									: {}),
							});
						}}
						aria-label="Platform"
					>
						<ToggleButton value={true}>Kopfzeile</ToggleButton>
						<ToggleButton value={false}>Adresshöhe</ToggleButton>
					</ToggleButtonGroup>
					<InputLabel id="Address alignment">Text</InputLabel>
					<ToggleButtonGroup
						color="primary"
						value={config.idRtfEnabled}
						exclusive
						onChange={(e, v) => {
							updatePdfTemplateConfiguration(template, {
								idRtfEnabled: v,
							});
						}}
						aria-label="Platform"
					>
						<ToggleButton value={false}>Standard</ToggleButton>
						<ToggleButton value={true}>Individuell</ToggleButton>
					</ToggleButtonGroup>
					{config.idRtfEnabled && (
						<PDFRtf
							value={config.idRtfText}
							controls={[
								[
									"italic",
									"underline",
									"bold",
									"alignLeft",
									"alignCenter",
									"alignRight",
								],
								["h1", "h2", "h3", "h4", "h5", "h6"],
								["image"],
							]}
							onSave={(val: string, width: number) => {
								if (!config.idRtfEnabled) return;
								updatePdfTemplateConfiguration(template, {
									idRtfText: {
										text: val,
										width: width,
									},
								});
							}}
						/>
					)}
					<FormControlLabel
						className="mb-1"
						control={
							<Checkbox
								checked={config.hideIds}
								onChange={(e) => {
									updatePdfTemplateConfiguration(template, {
										hideIds: e.target.checked,
									});
								}}
							/>
						}
						label="Ausblenden"
					/>
					<CustomTextFieldWithSaveButton
						label="Abstand nach oben in mm"
						value={config.idNumbersGap}
						onSave={(value) => {
							updatePdfTemplateConfiguration(template, {
								idNumbersGap: Number(value),
							});
						}}
						validationError={null}
						fullWidth
					/>
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="item-4">
				<AccordionTrigger>Mitgliedschaften</AccordionTrigger>
				<AccordionContent>
					<div className="flex flex-col border-b gap-4 mt-4 pb-4">
						<div className="flex flex-row">
							<FormGroup>
								<FormControlLabel
									control={
										<Checkbox
											checked={config.showVzlsMembership}
											onChange={(e) => {
												updatePdfTemplateConfiguration(
													template,
													{
														showVzlsMembership:
															e.target.checked,
													}
												);
											}}
										/>
									}
									label="VZLS"
								/>
							</FormGroup>
							<FormGroup>
								<FormControlLabel
									control={
										<Checkbox
											checked={config.showSwissLabel}
											onChange={(e) => {
												updatePdfTemplateConfiguration(
													template,
													{
														showSwissLabel:
															e.target.checked,
													}
												);
											}}
										/>
									}
									label="Swiss Label"
								/>
							</FormGroup>
							<FormGroup>
								<FormControlLabel
									control={
										<Checkbox
											checked={config.showSZVLabel}
											onChange={(e) => {
												updatePdfTemplateConfiguration(
													template,
													{
														showSZVLabel:
															e.target.checked,
													}
												);
											}}
										/>
									}
									label="SZV"
								/>
							</FormGroup>
							<Button>
								<label
									className="cursor-pointer"
									htmlFor="icon-upload"
								>
									{config.customImgUrl
										? "Ändern"
										: "Individuell"}
									<input
										type="file"
										className="hidden"
										id="icon-upload"
										accept=".png, .jpg, .jpeg"
										onChange={(e) => {
											const file = e.target.files?.[0];

											if (!file) return;

											if (file.size > 5 * 1024 * 1024) {
												showNotification({
													message:
														"Bitte wählen Sie eine Datei kleiner als 5MB",
													type: "error",
												});

												return;
											}

											const reader = new FileReader();

											reader.onloadend = () => {
												setCustomImg(
													reader.result as string
												);
											};

											reader.readAsDataURL(file);

											upload(file);
										}}
									/>
								</label>
							</Button>
						</div>
						<div className="flex w-full items-center flex-col gap-1 justify-center">
							<img
								style={{
									opacity: customImg ? 1 : 0.2,
									border: "1px solid black",
									height: "60px",
									width: "60px",
								}}
								alt="logo"
								src={
									customImg ? config.customImgUrl : emptyImage
								}
								className="h-full object-cover aspect-auto"
							/>
							<Button
								disabled={!config.customImgUrl}
								className="w-max "
								sx={{
									fontSize: "10px",
								}}
								onClick={async () => {
									setCustomImg(null);
									await updatePdfTemplateConfiguration(
										template,
										{
											customIconPath: "",
											customImgUrl: "",
										}
									);
								}}
							>
								Zurücksetzen
							</Button>
						</div>
						<div className="flex flex-col gap-2">
							<InputLabel id="Address alignment">
								Platzierung
							</InputLabel>
							<ToggleButtonGroup
								color="primary"
								value={config.organizationsImgPosition}
								exclusive
								onChange={(e, v) => {
									updatePdfTemplateConfiguration(template, {
										organizationsImgPosition: v as
											| "top"
											| "bottom",
									});
									if (v === "bottom") {
										updatePdfTemplateConfiguration(
											template,
											{
												customFooterEnabled: true,
											}
										);
									}
								}}
								aria-label="Platform"
							>
								<ToggleButton value="top">
									Adresshöhe
								</ToggleButton>
								<ToggleButton value="bottom">
									Fusszeile
								</ToggleButton>
							</ToggleButtonGroup>
						</div>
						{config.organizationsImgPosition === "top" ? (
							<div className="flex flex-col gap-2">
								<InputLabel id="Address alignment">
									Ausrichtung
								</InputLabel>
								<ToggleButtonGroup
									color="primary"
									value={config.organizationsImgAlignment}
									exclusive
									onChange={(e, v) => {
										updatePdfTemplateConfiguration(
											template,
											{
												organizationsImgAlignment:
													v as Alignment,
											}
										);
									}}
									aria-label="Platform"
								>
									<ToggleButton value="left">
										Links
									</ToggleButton>
									<ToggleButton value="right">
										Rechts
									</ToggleButton>
								</ToggleButtonGroup>
							</div>
						) : (
							<div className="text-xs text-gray-500 bg-blue-100 p-2 rounded-md w-72 flex items-center gap-2">
								<Info className="w-6 h-6" />
								Die Ausrichtung muss im Bereich "Individuelle
								Fusszeile" angepasst werden.
							</div>
						)}
						<div>
							<div>Text</div>
							<RichTextEditor
								style={{
									width: "100%",
									maxWidth: "350px",
									marginBottom: "24px",
								}}
								controls={[["italic", "underline", "bold"]]}
								value={config.orgImgText}
								onChange={(e) =>
									updatePdfTemplateConfiguration(template, {
										orgImgText: e,
									})
								}
							/>
						</div>
						<FormControlLabel
							className="mb-1"
							control={
								<Checkbox
									checked={config.hideMembers}
									onChange={(e) => {
										updatePdfTemplateConfiguration(
											template,
											{
												hideMembers: e.target.checked,
											}
										);
									}}
								/>
							}
							label="Ausblenden"
						/>
					</div>
				</AccordionContent>
			</AccordionItem>
			{template === "general" && (
				<AccordionItem value="item-7">
					<AccordionTrigger>Individuelle Fusszeile</AccordionTrigger>
					<AccordionContent>
						<div className="flex flex-col gap-4 mt-4 pb-4">
							<FormControlLabel
								control={
									<Checkbox
										checked={
											config.customFooterEnabled || false
										}
										onChange={(e) => {
											updatePdfTemplateConfiguration(
												template,
												{
													customFooterEnabled:
														e.target.checked,
												}
											);
										}}
									/>
								}
								label="Individuelle Fusszeile anzeigen"
							/>
							{config.organizationsImgPosition !== "bottom" ? (
								<>
									<div className="flex flex-col gap-2">
										<InputLabel>Fusszeilentyp</InputLabel>
										<ToggleButtonGroup
											color="primary"
											value={
												pdfTemplateConfiguration
													?.general
													.customFooterType || "text"
											}
											exclusive
											onChange={(e, v) => {
												if (v) {
													updatePdfTemplateConfiguration(
														template,
														{
															customFooterType:
																v as
																	| "text"
																	| "image",
														}
													);
												}
											}}
										>
											<ToggleButton value="text">
												Text
											</ToggleButton>
											<ToggleButton value="image">
												Bild
											</ToggleButton>
										</ToggleButtonGroup>
									</div>

									{pdfTemplateConfiguration?.general
										.customFooterType === "text" && (
										<div className="flex flex-col gap-2">
											<InputLabel>
												Fusszeilentext
											</InputLabel>
											<RichTextEditor
												style={{
													width: "100%",
													maxWidth: "600px",
													marginBottom: "16px",
												}}
												controls={[
													[
														"italic",
														"underline",
														"bold",
														"alignLeft",
														"alignCenter",
														"alignRight",
													],
												]}
												value={
													pdfTemplateConfiguration
														?.general
														.customFooterText || ""
												}
												onChange={(value) =>
													updatePdfTemplateConfiguration(
														template,
														{
															customFooterText:
																value,
														}
													)
												}
											/>
										</div>
									)}

									{pdfTemplateConfiguration?.general
										.customFooterType === "image" && (
										<>
											<div className="flex flex-col gap-2">
												<InputLabel>
													Fusszeilenbild
												</InputLabel>
												<Button variant="outlined">
													<label
														className="cursor-pointer"
														htmlFor="footer-image-upload"
													>
														{pdfTemplateConfiguration
															?.general
															.customFooterImageUrl
															? "Bild ändern"
															: "Bild hochladen"}
														<input
															type="file"
															className="hidden"
															id="footer-image-upload"
															accept=".png, .jpg, .jpeg"
															onChange={async (
																e
															) => {
																const file =
																	e.target
																		.files?.[0];
																if (!file)
																	return;

																if (
																	file.size >
																	5 *
																		1024 *
																		1024
																) {
																	showNotification(
																		{
																			message:
																				"Bitte wählen Sie eine Datei kleiner als 5MB",
																			type: "error",
																		}
																	);
																	return;
																}

																const path = `${organization?.id}/${uuidv4()}/${
																	file.name
																}`;
																const {
																	success,
																	url,
																} =
																	await uploadFileWithPublicUrl(
																		path,
																		file
																	);

																if (
																	success &&
																	url
																) {
																	updatePdfTemplateConfiguration(
																		template,
																		{
																			customFooterImageUrl:
																				url,
																		}
																	);
																}
															}}
														/>
													</label>
												</Button>
												{pdfTemplateConfiguration
													?.general
													.customFooterImageUrl && (
													<div className="flex flex-col items-center gap-2">
														<img
															src={
																pdfTemplateConfiguration
																	?.general
																	.customFooterImageUrl
															}
															alt="Footer preview"
															style={{
																maxHeight:
																	"60px",
																maxWidth:
																	"200px",
																border: "1px solid #ccc",
															}}
														/>
														<Button
															size="small"
															onClick={() => {
																updatePdfTemplateConfiguration(
																	template,
																	{
																		customFooterImageUrl:
																			"",
																	}
																);
															}}
														>
															Bild entfernen
														</Button>
													</div>
												)}
											</div>

											<div className="flex flex-col gap-2">
												<InputLabel>
													Bildgrösse
												</InputLabel>
												<ToggleButtonGroup
													color="primary"
													value={
														pdfTemplateConfiguration
															?.general
															.customFooterImageWidth ||
														"full"
													}
													exclusive
													onChange={(e, v) => {
														if (v) {
															updatePdfTemplateConfiguration(
																template,
																{
																	customFooterImageWidth:
																		v as
																			| "auto"
																			| "full"
																			| "custom",
																}
															);
														}
													}}
												>
													<ToggleButton value="auto">
														Automatisch
													</ToggleButton>
													<ToggleButton value="full">
														Vollbreite
													</ToggleButton>
													<ToggleButton value="custom">
														Benutzerdefiniert
													</ToggleButton>
												</ToggleButtonGroup>
											</div>

											{pdfTemplateConfiguration?.general
												.customFooterImageWidth ===
												"custom" && (
												<CustomTextFieldWithSaveButton
													label="Bildbreite (mm)"
													value={
														pdfTemplateConfiguration
															?.general
															.customFooterImageCustomWidth ||
														100
													}
													onSave={(value) => {
														updatePdfTemplateConfiguration(
															template,
															{
																customFooterImageCustomWidth:
																	parseInt(
																		value
																	) || 100,
															}
														);
													}}
													validationError={null}
													fullWidth
												/>
											)}
										</>
									)}
								</>
							) : (
								<div className="text-xs text-gray-500 bg-blue-100 p-2 rounded-md w-100 flex items-center gap-4">
									<Info className="w-6 h-6" />
									Die individuelle Fusszeile wird genutzt um
									die Mitgliedschaften anzuzeigen. Zum
									Anzeigen eigener Bilder oder Texte muss die
									Mitgliedschaft ausgeblendet oder auf
									Adresshöhe platziert werden.
								</div>
							)}

							<div className="flex flex-col gap-2">
								<InputLabel>Ausrichtung</InputLabel>
								<ToggleButtonGroup
									color="primary"
									value={
										pdfTemplateConfiguration?.general
											.customFooterAlignment || "center"
									}
									exclusive
									onChange={(e, v) => {
										if (v) {
											updatePdfTemplateConfiguration(
												template,
												{
													customFooterAlignment:
														v as Alignment,
												}
											);
										}
									}}
								>
									<ToggleButton value="left">
										Links
									</ToggleButton>
									<ToggleButton value="center">
										Mitte
									</ToggleButton>
									<ToggleButton value="right">
										Rechts
									</ToggleButton>
								</ToggleButtonGroup>
							</div>
							<FormControlLabel
								control={
									<Checkbox
										checked={
											pdfTemplateConfiguration?.general
												.customFooterIgnoreLayout
										}
										onChange={(e) => {
											updatePdfTemplateConfiguration(
												template,
												{
													customFooterIgnoreLayout:
														e.target.checked,
												}
											);
										}}
									/>
								}
								label="Ränder ignorieren"
							/>
							<FormControlLabel
								control={
									<Checkbox
										checked={
											pdfTemplateConfiguration?.general
												.customFooterOnlyFirstPage
										}
										onChange={(e) => {
											updatePdfTemplateConfiguration(
												template,
												{
													customFooterOnlyFirstPage:
														e.target.checked,
												}
											);
										}}
									/>
								}
								label={
									<div className="flex items-center gap-2">
										<span>
											Nur auf erster Seite anzeigen
										</span>
										<Tooltip title="Diese Option ist bei Rechnungen mit QR-Code automatisch aktiviert.">
											<Info className="w-4 h-4" />
										</Tooltip>
									</div>
								}
							/>

							<CustomTextFieldWithSaveButton
								label="Fusszeilenhöhe (mm)"
								value={
									pdfTemplateConfiguration?.general
										.customFooterHeight || 15
								}
								onSave={(value) => {
									updatePdfTemplateConfiguration(template, {
										customFooterHeight:
											parseInt(value) || 15,
									});
								}}
								validationError={null}
								fullWidth
							/>
						</div>
					</AccordionContent>
				</AccordionItem>
			)}
		</>
	);
};
